import React, { useState, useRef, useEffect } from "react"; // Added useEffect
import { Form, Row, Col } from "react-bootstrap";
import BackButton from "../../../../components/UI_Kit/BackButton/BackButton";
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";
import EXIF from "exif-js";
import TypeOneForm from "./forms/TypeOneForm";
import TypeTwoForm from "./forms/TypeTwoForm";
import TypeThreeForm from "./forms/TypeThreeForm";
import TypeFourForm from "./forms/TypeFourForm";
import api from "../../../../services/api"; // Import your api service instance
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DropdownComponent from "../../../../components/UI_Kit/Dropdown/Dropdown";

import "../../styles.css";

// Mapping of point type display names to their respective keys from API/config
// Note: Adjust the keys ("Nouveau point de vision", etc.) if they differ in your actual API response
const pointTypeKeys = {
  "Nouveau point de vision": "vision_point",
  "Point Relais": "relay_point",
  "Point Haut": "high_point",
  "Centre de surveillance": "surveillance_center",
};

const ViewpointForm = ({ onNext, initialData = {} }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [editedMetadata, setEditedMetadata] = useState({
    name: "",
    location: "",
  });
  const [showMenu, setShowMenu] = useState(null);

  // State for fetched configurations, loading, and errors
  const [pointConfigurations, setPointConfigurations] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pointType, setPointType] = useState(initialData.pointType || "");
  // const [gpsCoordinatesString, setGpsCoordinatesString] = useState(initialData.gpsCoordinatesString || ""); // REMOVE this line

  // Initialize form data based on initialData or defaults
  const [visionPointData, setVisionPointData] = useState({
    pointName: initialData.pointName || "",
    photos: initialData.photos || [],
    supportType: initialData.supportType || "",
    installationType: initialData.installationType || "",
    powerSupplyType: initialData.powerSupplyType || "",
    comment: initialData.comment || "",
  });

  const [relayPointData, setRelayPointData] = useState({
    pointName: initialData.pointName || "",
    photos: initialData.photos || [],
    supportType: initialData.supportType || "",
    installationType: initialData.installationType || "",
    powerSupplyType: initialData.powerSupplyType || "",
    comment: initialData.comment || "",
    hasCameras: initialData.hasCameras || "",
  });

  const [highPointData, setHighPointData] = useState({
    pointName: initialData.pointName || "",
    photos: initialData.photos || [], // Assuming photos start empty if not in initialData
    supportType: initialData.supportType || "",
    installationType: initialData.installationType || "",
    powerSupplyType: initialData.powerSupplyType || "",
    comment: initialData.comment || "",
    coverageArea: initialData.coverageArea || "",
  });

  const [surveillanceCenterData, setSurveillanceCenterData] = useState({
    pointName: initialData.pointName || "",
    photos: initialData.photos || [], // Assuming photos start empty if not in initialData
    supportType: initialData.supportType || "",
    installationType: initialData.installationType || "",
    powerSupplyType: initialData.powerSupplyType || "",
    comment: initialData.comment || "",
    recorderSize: initialData.recorderSize || "",
    screenSize: initialData.screenSize || "",
  });

  const fileInputRef = useRef(null);

  // Fetch data on component mount
  useEffect(() => {
    const fetchPointConfigurations = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Get the access token from localStorage
        const token = localStorage.getItem("access_token");
        if (!token) {
          setError("Authentication token not found. Please log in.");
          setIsLoading(false);
          return; // Stop execution if no token
        }

        // Make the API call using the api service instance
        const response = await api.get("/prices", {
          // Use the /prices endpoint
          headers: {
            Authorization: `Bearer ${token}`, // Add the Authorization header
          },
        });

        const data = response.data; // Use the actual API response data

        // Transform API response (Keep this logic as is)
        const transformedConfigs = data.reduce((acc, item) => {
          const customKey = pointTypeKeys[item.type]; // Use the mapping
          if (customKey) {
            acc[customKey] = {
              type: item.type, // Store the original display name if needed
              fields: item.champ.reduce((fieldAcc, field) => {
                fieldAcc[field.titre] = field.choix.map(
                  (choice) => choice.titre
                );
                return fieldAcc;
              }, {}),
            };
          } else {
            console.warn(`No key mapping found for point type: ${item.type}`);
          }
          return acc;
        }, {});

        setPointConfigurations(transformedConfigs);
      } catch (err) {
        console.error("Error fetching point configurations:", err);
        // Provide more specific error feedback if possible
        if (err.response && err.response.status === 401) {
          setError("Authentication failed. Please log in again.");
        } else {
          setError("Failed to load configurations. Please try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPointConfigurations();
  }, []); // Empty dependency array ensures this runs only once on mount

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "pointType") {
      setPointType(value);
      // Optionally reset other fields when type changes
    // REMOVE this else if block for gpsCoordinatesString
    // } else if (name === "gpsCoordinatesString") { 
    //     setGpsCoordinatesString(value);
    } else {
      // Update the correct state based on the current pointType
      switch (pointType) {
        case "vision_point":
          setVisionPointData((prev) => ({ ...prev, [name]: value }));
          break;
        case "relay_point":
          setRelayPointData((prev) => ({ ...prev, [name]: value }));
          break;
        case "high_point":
          setHighPointData((prev) => ({ ...prev, [name]: value }));
          break;
        case "surveillance_center":
          setSurveillanceCenterData((prev) => ({ ...prev, [name]: value }));
          break;
        default:
          // Handle case where pointType is not set or invalid?
          break;
      }
    }
  };

  const getPhotoLocation = (file) => {
    return new Promise((resolve) => {
      // Check if it's a HEIC file
      if (file.name && file.name.toLowerCase().endsWith('.heic')) {
        // For HEIC files, use ExifReader
        const reader = new FileReader();
        reader.onload = async function(e) {
          try {
            // Import ExifReader dynamically to avoid issues
            const ExifReader = await import('exifreader');
            const tags = ExifReader.default.load(e.target.result);
            
            // Check for GPS data
            if (tags.GPSLatitude && tags.GPSLongitude) {
              const latRef = tags.GPSLatitudeRef ? tags.GPSLatitudeRef.value[0] : 'N';
              const lonRef = tags.GPSLongitudeRef ? tags.GPSLongitudeRef.value[0] : 'E';
              
              // Extract latitude and longitude values
              const latitude = tags.GPSLatitude.description;
              const longitude = tags.GPSLongitude.description;
              
              // Store the raw values for later use
              file.exifData = {
                latitude: latitude,
                longitude: longitude,
                latitudeRef: latRef,
                longitudeRef: lonRef
              };
              
              resolve(`${latitude}° ${latRef}, ${longitude}° ${lonRef}`);
            } else {
              console.log('No GPS data found in HEIC file');
              resolve(""); // No GPS data
            }
          } catch (error) {
            console.error('Error extracting HEIC metadata:', error);
            resolve(""); // Error handling
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        // For other file types, use the existing EXIF.js method
        EXIF.getData(file, function () {
          const lat = EXIF.getTag(this, "GPSLatitude");
          const lon = EXIF.getTag(this, "GPSLongitude");
          const latRef = EXIF.getTag(this, "GPSLatitudeRef");
          const lonRef = EXIF.getTag(this, "GPSLongitudeRef");
      
          if (lat && lon && latRef && lonRef) {
            const latitude = convertDMSToDD(lat[0], lat[1], lat[2], latRef);
            const longitude = convertDMSToDD(lon[0], lon[1], lon[2], lonRef);
            resolve(
              `${latitude.toFixed(4)}° ${latRef}, ${longitude.toFixed(
                4
              )}° ${lonRef}`
            );
          } else {
            resolve(""); // Return empty if no GPS data
          }
        });
      }
    });
  };

  const convertDMSToDD = (degrees, minutes, seconds, direction) => {
    let dd = degrees + minutes / 60 + seconds / 3600;
    if (direction === "S" || direction === "W") {
      dd = -dd;
    }
    return dd;
  };

  const handlePhotoUpload = async (event) => {
     const files = Array.from(event.target.files);
  
    const processPhotos = async (files) => {
      return Promise.all(
        files.map(async (file) => {
          const location = await getPhotoLocation(file);
          return {
            file: file, // Store the original file object
            url: URL.createObjectURL(file),
            name: file.name,
            location: location,
            // Preserve any exifData that was attached during getPhotoLocation
            exifData: file.exifData
          };
        })
      );
    };
  
    const newPhotos = await processPhotos(files);
    let updateFunction;
  
    switch (pointType) {
      case "vision_point":
        updateFunction = setVisionPointData;
        break;
      case "relay_point":
        updateFunction = setRelayPointData;
        break;
      case "high_point":
        updateFunction = setHighPointData;
        break;
      case "surveillance_center":
        updateFunction = setSurveillanceCenterData;
        break;
      default:
        return; // Or handle error
    }

    updateFunction((prev) => ({
      ...prev,
      photos: [...(prev.photos || []), ...newPhotos],
    }));
  };

  const handleDeletePhoto = (index) => {
    let updateFunction;
    switch (pointType) {
      case "vision_point":
        updateFunction = setVisionPointData;
        break;
      case "relay_point":
        updateFunction = setRelayPointData;
        break;
      case "high_point":
        updateFunction = setHighPointData;
        break;
      case "surveillance_center":
        updateFunction = setSurveillanceCenterData;
        break;
      default:
        return;
    }
    updateFunction((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
  };

  const handleReplacePhoto = async (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const newPhoto = {
        url: URL.createObjectURL(file),
        name: file.name,
        location: await getPhotoLocation(file),
      };

      let updateFunction;
      switch (pointType) {
        case "vision_point":
          updateFunction = setVisionPointData;
          break;
        case "relay_point":
          updateFunction = setRelayPointData;
          break;
        case "high_point":
          updateFunction = setHighPointData;
          break;
        case "surveillance_center":
          updateFunction = setSurveillanceCenterData;
          break;
        default:
          setShowModal(false); // Close modal even if type is somehow invalid
          return;
      }

      updateFunction((prev) => {
        const updatedPhotos = [...prev.photos];
        updatedPhotos[index] = newPhoto;
        return { ...prev, photos: updatedPhotos };
      });
    }
    setShowModal(false); // Close modal after replacement
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let submissionData;

    // Consolidate data based on pointType
    switch (pointType) {
      case "vision_point":
        submissionData = { pointType, ...visionPointData };
        break;
      case "relay_point":
        submissionData = { pointType, ...relayPointData };
        break;
      case "high_point":
        submissionData = { pointType, ...highPointData };
        break;
      case "surveillance_center":
        submissionData = { pointType, ...surveillanceCenterData };
        break;
      default:
        toast.info("Veuillez sélectionner un type de point");
        return;
    }

    // Basic validation (ensure pointName exists for the selected type)
    if (!submissionData.pointName) {
      // Find the pointName field within the correct data object
      let currentPointName = "";
      if (pointType === "vision_point")
        currentPointName = visionPointData.pointName;
      else if (pointType === "relay_point")
        currentPointName = relayPointData.pointName;
      else if (pointType === "high_point")
        currentPointName = highPointData.pointName;
      else if (pointType === "surveillance_center")
        currentPointName = surveillanceCenterData.pointName;

      if (!currentPointName) {
        toast.info("Veuillez entrer un nom de point");
        return;
      }
      // If pointName exists in the specific state but not submissionData (shouldn't happen with spread),
      // ensure it's included. This check might be redundant if state structure is consistent.
      submissionData.pointName = currentPointName;
    }

    console.log("Submitting data:", submissionData);
    onNext(submissionData);
  };

  const handleOpenModal = (index) => {
    setSelectedPhotoIndex(index);
    let photos;

    switch (pointType) {
      case "vision_point":
        photos = visionPointData.photos;
        break;
      case "relay_point":
        photos = relayPointData.photos;
        break;
      case "high_point":
        photos = highPointData.photos;
        break;
      case "surveillance_center":
        photos = surveillanceCenterData.photos;
        break;
      default:
        photos = []; // Default to empty array if type is invalid
    }

    // Ensure photos array and the specific photo exist before accessing properties
    if (photos && photos[index]) {
      setEditedMetadata({
        name: photos[index].name || "", // Provide default empty string
        location: photos[index].location || "", // Provide default empty string
      });
      setShowModal(true);
    } else {
      console.error(
        "Attempted to open modal for invalid photo index or point type."
      );
      // Optionally show an error to the user
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPhotoIndex(null);
    // Reset editedMetadata if needed
    setEditedMetadata({ name: "", location: "" });
  };

  const handleSaveMetadata = () => {
    // Regex mise à jour pour accepter les deux formats:
    // 1. Format DMS: -?XX.XXXX°? [NS], -?XX.XXXX°? [EW] (degrés et décimales optionnels)
    // 2. Format Simple: -?XX.XXXX, -?XX.XXXX
    const locationRegex = /^(-?\d+(\.\d+)?\s?°?\s?[NS]?\s?,\s?-?\d+(\.\d+)?\s?°?\s?[EW]?)$|^(-?\d+(\.\d+)?\s?,\s?-?\d+(\.\d+)?)$/i;

    if (
      editedMetadata.location && // Vérifie seulement si le champ n'est pas vide
      !locationRegex.test(editedMetadata.location.trim()) // Teste la chaîne nettoyée
    ) {
      toast.info(
        "Format de localisation invalide. Utilisez: 'XX.XXXX° N, XX.XXXX° E' ou 'XX.XXXX, XX.XXXX'"
      );
      return; // Arrête l'exécution si le format est invalide
    }

    if (selectedPhotoIndex !== null) {
      let updateFunction;
      switch (pointType) {
        case "vision_point":
          updateFunction = setVisionPointData;
          break;
        case "relay_point":
          updateFunction = setRelayPointData;
          break;
        case "high_point":
          updateFunction = setHighPointData;
          break;
        case "surveillance_center":
          updateFunction = setSurveillanceCenterData;
          break;
        default:
          handleCloseModal(); // Ferme la modale même si le type est invalide
          return;
      }

      updateFunction((prev) => {
        const updatedPhotos = [...prev.photos];
        // S'assure que la photo existe à l'index donné
        if (updatedPhotos[selectedPhotoIndex]) {
          updatedPhotos[selectedPhotoIndex] = {
            ...updatedPhotos[selectedPhotoIndex],
            name: editedMetadata.name,
            // Sauvegarde la localisation nettoyée (trim)
            location: editedMetadata.location.trim(),
          };
        }
        return { ...prev, photos: updatedPhotos };
      });
    }
    handleCloseModal(); // Ferme la modale après la sauvegarde
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setEditedMetadata((prev) => ({ ...prev, [name]: value }));
  };

  // Helper function to get current photos based on pointType
  const getCurrentPhotos = () => {
    switch (pointType) {
      case "vision_point":
        return visionPointData.photos;
      case "relay_point":
        return relayPointData.photos;
      case "high_point":
        return highPointData.photos;
      case "surveillance_center":
        return surveillanceCenterData.photos;
      default:
        return [];
    }
  };

  return (
    <>
          <ToastContainer />
      {/* Header */}
      <div className="header mb-4 d-flex align-items-center">
        <BackButton className="back-button" onClick={() => onNext(null)} />
        <span className="step-title">Configuration d'un point</span>
      </div>

      {/* Form */}
      <Form onSubmit={handleSubmit} className="mb-5 pb-5">
        {" "}
        {/* Point Type Selection */}
        <Row className="mb-3">
          <Col>
            <Form.Group controlId="pointType">
              <Form.Label>Type de point*</Form.Label>
              <DropdownComponent 
                name="pointType"
                options={[
                  { value: "vision_point", label: "Point de Vision" },
                  { value: "relay_point", label: "Point Relais" },
                  { value: "high_point", label: "Point Haut" },
                  { value: "surveillance_center", label: "Centre de Surveillance" }
                ]}
                value={pointType}
                onChange={handleChange}
                placeholder="Sélectionner..."
              />
            </Form.Group>
          </Col>
        </Row>
        
        {/* REMOVE the entire Row for GPS Coordinates String Input if it exists in your current code
        <Row className="mb-3">
          <Col>
            <Form.Group controlId="gpsCoordinatesString">
              <Form.Label>Coordonnées GPS (texte)</Form.Label>
              <Form.Control
                type="text"
                name="gpsCoordinatesString"
                // value={gpsCoordinatesString} // This would also be removed
                onChange={handleChange}
                placeholder="Ex: 48.8584° N, 2.2945° E ou 48.8584, 2.2945"
              />
            </Form.Group>
          </Col>
        </Row> 
        */}

        {/* Conditional Rendering of Forms */}
        {/* Add checks for isLoading and existence of config */}
        {isLoading && <p>Chargement des configurations...</p>}
        {error && <p className="text-danger">{error}</p>}

        {!isLoading && !error && pointType === "vision_point" && pointConfigurations.vision_point && (
          <TypeOneForm
            config={pointConfigurations.vision_point} // Pass the specific config as a prop
            type1Data={visionPointData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        )}
        {!isLoading && !error && pointType === "relay_point" && pointConfigurations.relay_point && (
          <TypeTwoForm
            config={pointConfigurations.relay_point} // Example for TypeTwoForm
            type2Data={relayPointData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        )}
        {!isLoading && !error && pointType === "high_point" && pointConfigurations.high_point && (
          <TypeThreeForm
            config={pointConfigurations.high_point} // Example for TypeThreeForm
            type3Data={highPointData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        )}
        {!isLoading && !error && pointType === "surveillance_center" && pointConfigurations.surveillance_center && (
          <TypeFourForm
            config={pointConfigurations.surveillance_center} // Example for TypeFourForm
            type4Data={surveillanceCenterData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
          />
        )}
        {/* Required Fields Note */}
        <p className="text-muted">*Champs obligatoires</p>
        {/* Footer Button */}
        <div className="footer shadow-lg d-flex justify-content-center">
          <button
            className="w-100 primary-button primary-button-block"
            type="submit"
          >
            Suivant
          </button>
        </div>
      </Form>

      {/* Confirmation Modal for Metadata */}
      <ConfirmationModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleSaveMetadata}
        title={
          selectedPhotoIndex !== null
            ? `Modifier Vue ${selectedPhotoIndex + 1}` // Changed title
            : "Modifier les métadonnées"
        }
        buttonTitle="Sauvegarder"

      >
        {selectedPhotoIndex !== null && (
          <>
            {/* Image Preview and Replace Button */}
            <div
              className="mb-3 position-relative"
              style={{
                maxWidth: "100%",
                maxHeight: "200px",
                overflow: "hidden",
              }} // Added overflow hidden
            >
              <img
                // Safely access the photo URL
                src={
                  getCurrentPhotos()[selectedPhotoIndex]?.url ||
                  "/placeholder.png"
                } // Added fallback
                alt={`Preview ${selectedPhotoIndex + 1}`} // Improved alt text
                style={{
                  width: "100%", // Changed to width
                  height: "200px", // Fixed height
                  objectFit: "cover",
                }}
              />
              <Form.Group
                controlId="photoReplace"
                className="position-absolute top-50 start-50 translate-middle text-center"
              >
                <Form.Label
                  className="photoReplaceText p-2 rounded d-flex flex-column align-items-center justify-content-center" // Added flex styles
                  htmlFor="photoReplaceInput"
                  style={{
                    cursor: "pointer",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                  }} 
                >
                  <img
                    src="/pen.png"
                    alt="Edit pic"
                    style={{ width: "24px", height: "24px" }}
                  ></img>{" "}
                  {/* Added size */}
                  <span className="mt-1">
                    {" "}
                    {/* Removed background */}
                    Modifier la photo
                  </span>
                </Form.Label>
                <Form.Control
                  id="photoReplaceInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleReplacePhoto(e, selectedPhotoIndex)}
                  style={{
                    opacity: 0,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    top: 0,
                    left: 0,
                    cursor: "pointer",
                  }}
                />
              </Form.Group>
            </div>

            {/* Metadata Fields */}
            {/* <Form.Group controlId="photoName" className="mb-3">
              <Form.Label>Nom de la vue</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={editedMetadata.name}
                onChange={handleMetadataChange}
                placeholder="Ex: Vue Entrée Principale"
              />
            </Form.Group> */}

            <Form.Group controlId="photoLocation">
              <Form.Label>Coordonnées GPS</Form.Label>
              <Form.Control
                type="text"
                name="location"
                value={editedMetadata.location}
                onChange={handleMetadataChange}
                placeholder="XX.XXXX° N/S, XX.XXXX° E/W"
              />
              <Form.Text className="text-muted d-flex align-items-start gap-2 mt-2">
                {" "}
                {/* Added mt-2 */}
                <img
                  src="/Info-circle.png"
                  alt="Hint"
                  style={{ width: "20px", height: "20px", marginTop: "0px" }}
                ></img>{" "}
                {/* Added size and margin */}
                <span className="text-muted-custom">
                  Rentrez l'adresse du bâtiment sur Google Maps, marquez
                  l'emplacement exact sur la carte et notez les coordonnées GPS
                  précises de la caméra.
                </span>
              </Form.Text>
            </Form.Group>
          </>
        )}
      </ConfirmationModal>
    </>
  );
};

export default ViewpointForm;